/* eslint-disable max-classes-per-file */
import APIClientBase from 'wootils/esm/shared/apiClient';
import 'yetch/polyfill';

/**
 * @external {HttpClient} https://aurelia.io/docs/api/fetch-client/class/HttpClient
 */

/**
 * @typedef {Object} APIClientConfiguration
 * @property {String}             url       The base URL of the API the client will make
 *                                          requests to.
 * @property {APIClientEndpoints} endpoints A dictionary of named endpoints relative to the API
 *                                          base URL.
 */

/**
 * A special class of error for the `APIClient` to use.
 * @extends {Error}
 */
export class APIException extends Error {
  /**
   * @param {String}  message The error message.
   * @param {Number}  status  The HTTP status of the request that caused the error.
   * @param {Boolean} aborted Whether or not the error was caused because the request was aborted.
   */
  constructor(message, status, aborted) {
    super(message);

    /**
     * The HTTP status of the request that caused the error.
     * @type {Number}
     */
    this.status = status;
    /**
     * Whether or not the error was caused because the request was aborted.
     * @type {Boolean}
     */
    this.aborted = aborted;
  }
}
/**
 * An abstract class for an API client that is a mix of the Aurelia HTTP client and the wootil's
 * API client.
 * The advantage of this class is that implement the Abort controller so the requests can be
 * aborted; and it also uses a {@link APIException} to store the response status when throwing
 * errors.
 * @abstract
 */
export class APIClient extends APIClientBase {
  /**
   * @param {HttpClient}             httpClient       The Aurelia's HTTP client, to make the
   *                                                  requests.
   * @param {APIClientConfiguration} apiConfiguration The configuration of the API the client will
   *                                                  make requests to.
   * @throws {TypeError} If instantiated without extending the class.
   * @abstract
   */
  constructor(httpClient, apiConfiguration) {
    if (new.target === APIClient) {
      throw new TypeError('APIClient is an abstract class, it can\'t be instantiated directly');
    }

    super(
      apiConfiguration.url,
      apiConfiguration.endpoints,
      httpClient.fetch.bind(httpClient),
    );

    /**
     * A local reference for the Aurelia's HTTP Client.
     * @type {HttpClient}
     * @access protected
     */
    this._httpClient = httpClient;
    /**
     * The configuration of the API the client will make requests to.
     * @type {APIClientConfiguration}
     */
    this._apiConfiguration = apiConfiguration;
  }
  /**
   * Formats a recevied error response using the {@link APIException}.
   * @param {Object} response The contents of the response.
   * @param {Number} status   The HTTP status of the response.
   * @return {APIException}
   */
  error(response, status) {
    return new APIException(
      this._getMessageFromResponse(response),
      status,
      false,
    );
  }
  /**
   * Makes a request.
   * @param {Object}  options         The request options.
   * @param {string}  options.url     The request URL.
   * @param {string}  options.method  The request method. `GET` by default.
   * @param {Object}  options.body    A request body to send.
   * @param {Object}  options.headers The request headers.
   * @param {boolean} options.json    Whether or not the response should _"JSON decoded"_. `true`
   *                                  by default.
   * @return {Promise<Object,APIException>}
   */
  fetch(options) {
    const abortController = new AbortController();
    const newOptions = Object.assign({}, options, {
      signal: abortController.signal,
    });

    const request = super.fetch(newOptions);

    const requestProto = Object.getPrototypeOf(request);
    Object.defineProperty(requestProto, 'abort', {
      value: abortController.abort.bind(abortController),
      writable: true,
    });

    return request
    .then((response) => response.data || response)
    .catch((error) => {
      let nextError;
      if (error.name === 'AbortError') {
        nextError = new APIException('Request aborted', 0, true);
      } else {
        nextError = error;
      }

      return Promise.reject(nextError);
    });
  }
  /**
   * Helper method that tries to get an error message from a given response.
   * @param {Object} response The contents of the response.
   * @return {String}
   * @access protected
   */
  _getMessageFromResponse(response) {
    let message;
    if (response.error) {
      message = response.error;
    } else if (response.data && response.data && response.data.message) {
      ({ message } = response.data);
    } else if (response.data && response.data && response.data.error) {
      message = response.data.error;
    } else {
      message = 'Unexpected error';
    }

    return message;
  }
}
